import { useEffect, useState, useRef } from 'react';
import Markdown from 'react-markdown'
import { useOktaAuth } from '@okta/okta-react';
import AIService from '../services/aiService';
import Icon from '@msi/cobalt-react/icon';
import DropDown from '../components/DropDown/DropDown';
import { useDarkTheme } from '../contexts/DarkThemeContext';
import './chat_with_ai.css';

// Define the chat message interface
interface ChatMessage {
  message: string;
  role: 'user' | 'model';
  feedback: 'positive' | 'negative' | null;
}

// Add keyframe animations
const fillAnimation = `
  @keyframes rotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes fade-out {
    0% { opacity: 1; }
    50% { opacity: 0.3; }
    100% { opacity: 1; }
  }
  
  @keyframes fade-in {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
`;

const AI_LOGS_POLLING_INTERVAL = 500;

// Reusable feedback spinner component
const FeedbackSpinner = () => (
  <div className="feedback-spinner">
    <svg width="16" height="16" viewBox="0 0 16 16">
      <circle cx="8" cy="8" r="6" stroke="#007AAA" strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="28" strokeDashoffset="21" style={{animation: 'rotate 1s linear infinite'}} />
    </svg>
  </div>
);

function ChatWithAI() {
  const { isDarkTheme } = useDarkTheme();
  const [message, setMessage] = useState<string>('');

  // Dark theme color helpers
  const colors = {
    bg: isDarkTheme ? '#000912' : undefined,
    text: isDarkTheme ? '#fff' : undefined,
    chatBg: isDarkTheme ? '#0d1f2f' : '#f9f9f9',
    border: isDarkTheme ? 'rgba(255,255,255,0.12)' : '#ccc',
    aiBubble: isDarkTheme ? '#1a3344' : '#0000000D',
    userBubble: isDarkTheme ? '#326076' : '#007AAA1A',
    msgText: isDarkTheme ? '#fff' : '#000',
    btnBg: isDarkTheme ? '#142737' : '#fff',
    btnText: isDarkTheme ? '#28b1ec' : '#007AAA',
    btnBorder: isDarkTheme ? '#28b1ec' : '#007AAA',
    inputBg: isDarkTheme ? '#001526' : undefined,
    inputText: isDarkTheme ? '#fff' : undefined,
    copyIconColor: isDarkTheme ? '#e0e0e0' : '#0c0c0cff',
  };
  const [aiThinkingLog, setAiThinkingLogs] = useState<string[]>([]);
  const [chatHistory, setChatHistory] = useState<(string | ChatMessage)[]>([]);
  const [sessionId, setSessionId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allChats, setAllChats] = useState<string[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState<{ [key: number]: boolean }>({});
  const [copiedMessages, setCopiedMessages] = useState<{ [key: number]: boolean }>({});
  const [shouldAutoScroll, setShouldAutoScroll] = useState<boolean>(true);
  const timeoutsRef = useRef<number[]>([]);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const { authState } = useOktaAuth();
  const aiService = new AIService();
  const suggestions = [
    "Can you share the details of 'Get Detections' API?",
    "Who would be the point of contact for Rave team?",
    "Are there any license plate recognition APIs in the ecosystem?",
  ];

  // Helper function to get message text from either string or object format
  const getMessageText = (chatItem: any): string => {
    return aiService.cleanMessageForDisplay(chatItem);
  };

  // Helper function to determine if this is an AI message
  const isAIMessage = (message: string | ChatMessage) => {
    if (typeof message === 'object' && message.role) {
      return message.role === 'model';
    }
    // Fallback for legacy string format
    const messageText = typeof message === 'string' ? message : message.message;
    return messageText.startsWith('AI:');
  };    // This effect runs once on component mount to load the session and history
  
  useEffect(() => {
    let currentChatId = localStorage.getItem('currentChatId');
    if (!currentChatId) {
      currentChatId = `chat-${Date.now()}`;
      localStorage.setItem('currentChatId', currentChatId);
    }
    setSessionId(currentChatId);

    const loadedHistory = aiService.getChatUiHistory(currentChatId);
    if (loadedHistory.length > 0) {
      setChatHistory(loadedHistory);
      // Auto-scroll to bottom when loading initial chat
      setShouldAutoScroll(true);
    }

    setAllChats(aiService.getAllChatIds());
  }, []); // Empty array ensures this runs only once

  // Poll for logs in real-time while AI is processing
  useEffect(() => {
    if (!isLoading || !sessionId) return;

    const pollInterval = setInterval(async () => {
      try {
        const logs = await aiService.getAiThinkingLogs(sessionId, authState);
        if (logs && logs.length > 0) {
          setAiThinkingLogs(logs);
        }
      } catch (error) {
        console.error('Error polling logs:', error);
      }
    }, AI_LOGS_POLLING_INTERVAL);

    return () => clearInterval(pollInterval);
  }, [isLoading, sessionId]);

  // Auto-scroll to bottom when chat history changes (only when we want it to)
  useEffect(() => {
    if (shouldAutoScroll && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
    // Reset auto-scroll flag after use
    if (shouldAutoScroll) {
      setShouldAutoScroll(false);
    }
  }, [chatHistory, shouldAutoScroll]);

  const handleSendMessage = (msg: string) => {
    setIsLoading(true);
    // Clear search logs for new message
    setAiThinkingLogs([]);

    // Clear any existing timeouts
    timeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
    timeoutsRef.current = [];


    if (msg.trim()) {
      // Add user message and loading placeholder in a single state update
      const userMessage: ChatMessage = {
        message: `You: ${msg}`,
        role: 'user',
        feedback: null
      };
      
      const loadingMessage: ChatMessage = {
        message: 'AI: Loading...',
        role: 'model',
        feedback: null
      };
      
      setChatHistory(prev => [...prev, userMessage, loadingMessage]);
      // Enable auto-scroll for new messages
      setShouldAutoScroll(true);
      setMessage('');

      setAiThinkingLogs(['Thinking...']);
      
      // Send message to AI service
      aiService.chatWithAi(sessionId, msg, authState).then(response => {
        // Validate the response message
        const aiMessage = response.message || 'Sorry, I could not generate a response.';
        setChatHistory((prev) => {
          const aiResponse: ChatMessage = {
            message: `AI: ${aiMessage}`,
            role: 'model',
            feedback: null
          };
          
          const newHistory = [
            ...prev.slice(0, prev.length - 1), // Remove loading message
            aiResponse
          ];
          
          // Save the updated history to localStorage
          aiService.saveChatUiHistory(sessionId, newHistory);
          
          return newHistory;
        });
          
        // Enable auto-scroll for AI response
        setShouldAutoScroll(true);
      }).catch(error => {
        console.error('Error:', error);
        setChatHistory((prev) => {
          const errorResponse: ChatMessage = {
            message: 'AI: Sorry, I could not process your request.',
            role: 'model',
            feedback: null
          };
          
          const newHistory = [
            ...prev.slice(0, prev.length - 1), // Remove loading message
            errorResponse
          ];
          
          // Save the updated history to localStorage
          aiService.saveChatUiHistory(sessionId, newHistory);
          
          return newHistory;
        });
        
        // Enable auto-scroll for error message
        setShouldAutoScroll(true);
        setAiThinkingLogs(['AI failed to process your request. Please try again later.']);
      }).finally(() => {
        setIsLoading(false);
      });
    }
  };

  const handleNewChat = () => {
    const newChatId = `chat-${Date.now()}`;
    localStorage.setItem('currentChatId', newChatId);
    setSessionId(newChatId);
    setChatHistory([]);
    setMessage('');
    setAllChats([newChatId, ...allChats]);
    setAiThinkingLogs([]);
    // Clear loading states
    setFeedbackLoading({});
    setCopiedMessages({});
    // Don't auto-scroll for empty new chat
    setShouldAutoScroll(false);
  };

  const handleSelectChat = (chatId: string) => {
    localStorage.setItem('currentChatId', chatId);
    setSessionId(chatId);
    setChatHistory(aiService.getChatUiHistory(chatId));
    localStorage.setItem('currentChatId', chatId);
    // Clear loading states when switching chats
    setFeedbackLoading({});
    setCopiedMessages({});
    // Auto-scroll to bottom when loading existing chat
    setShouldAutoScroll(true);
  };

  const generateChatName = (chatId: string): string => {
    const arr = JSON.parse(localStorage.getItem("chatUIHistory_"+chatId) || '[]');
    const firstMsg = typeof arr[0] === 'string' ? arr[0] : '';  
    if (firstMsg.length > 40) {
      return `${firstMsg.slice(0, 37)}...`;
    }
    return firstMsg ? `${firstMsg}` : `Chat from ${new Date(parseInt(chatId.split('-')[1])).toLocaleString()}`;
  }

  const printLogs = (logs: string[]): any => {
    if (logs.length === 0) {
      return null; // Don't render anything if no logs
    }
    
    return (
      <div style={{ position: 'absolute', top: 0, left: 0, alignItems: 'left', display: 'flex', flexDirection: 'column', color: isDarkTheme ? '#5cc8f0' : '#007AAA', minWidth: 'fit-content', width:'350px', textDecoration: 'none', textAlign: 'left'}}>
        {logs.map((log, index) => (
          <div key={index} style={{ 
            marginBottom: '5px',
            animation: 'fade-out 3.5s linear infinite',
            // Add animation delay based on index - later logs fade in later
            animationDelay: `${index * 0.2}s`
          }}>
            {log.trim()}
          </div>
        ))}
      </div>
    );
  };

  const handleFeedback = async (chatIndex: number, feedbackValue: 'positive' | 'negative') => {
    const currentChat = chatHistory[chatIndex];
    
    // Ensure we're only applying feedback to AI messages using role property
    if (!currentChat || typeof currentChat !== 'object' || currentChat.role !== 'model') {
      console.error('Feedback can only be applied to AI messages');
      return;
    }
    
    // Check if feedback is already being processed for this message
    if (feedbackLoading[chatIndex]) {
      return;
    }
    
    // Set loading state for this specific message
    setFeedbackLoading(prev => ({ ...prev, [chatIndex]: true }));
    
    const previousChat = chatIndex > 0 ? chatHistory[chatIndex - 1] : null;
    
    // Get clean message for checking if it's AI response
    const currentMessage = aiService.cleanMessageForDisplay(currentChat);
    if (!currentMessage || !currentMessage.startsWith('AI:')) {
      console.error('Invalid AI message format');
      setFeedbackLoading(prev => ({ ...prev, [chatIndex]: false }));
      return;
    }

    // Extract user prompt and AI response (clean version without feedback metadata)
    const cleanAiResponse = currentMessage.substring(3); // Remove "AI:" prefix
    
    // Find the user prompt by looking backwards for the previous user message
    let userPrompt = '';
    if (previousChat && typeof previousChat === 'object' && previousChat !== null && previousChat.role === 'user') {
      const previousMessage = aiService.cleanMessageForDisplay(previousChat);
      userPrompt = previousMessage.startsWith('You:') ? previousMessage.substring(4) : '';
    }

    if (!userPrompt) {
      console.error('Could not find user prompt for feedback');
      setFeedbackLoading(prev => ({ ...prev, [chatIndex]: false }));
      return;
    }

    try {
      // Send feedback to backend
      await aiService.sendFeedback(userPrompt, cleanAiResponse, feedbackValue, authState);
      
      // Update chat history with feedback
      aiService.updateChatHistoryWithFeedback(sessionId, chatIndex, feedbackValue);
      
      // Reload the chat history to reflect the feedback
      const updatedHistory = aiService.getChatUiHistory(sessionId);
      setChatHistory(updatedHistory);
      
      console.log('Feedback sent successfully');
    } catch (error) {
      console.error('Failed to send feedback:', error);
    } finally {
      // Remove loading state
      setFeedbackLoading(prev => ({ ...prev, [chatIndex]: false }));
    }
  };

  const handleCopy = async (messageText: string, chatIndex: number) => {
    try {
      await navigator.clipboard.writeText(messageText);
      
      // Show visual feedback
      setCopiedMessages(prev => ({ ...prev, [chatIndex]: true }));
      
      // Remove the feedback after 2 seconds
      setTimeout(() => {
        setCopiedMessages(prev => ({ ...prev, [chatIndex]: false }));
      }, 2000);
      
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: colors.bg, color: colors.text}}>
      {/* Add the style tag with keyframes */}
      <style dangerouslySetInnerHTML={{ __html: fillAnimation }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>API Concierge</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ minWidth: '200px' }}>
          <DropDown
            id="previous-chats"
            value={sessionId}
            options={allChats.map(id => ({
              id: id,
              name: generateChatName(id)
            }))}
            onOptionSelect={handleSelectChat}
            style={{ minWidth: '200px' }}
          />
          </div>
          <button
            onClick={handleNewChat}
            disabled={isLoading}
            style={{
              padding: '6px 12px',
              border: `1px solid ${colors.btnBorder}`,
              borderRadius: '2px',
              backgroundColor: colors.btnBg,
              color: colors.btnText,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            + New Chat
          </button>
          
        </div>
      </div>
         <div
          ref={chatContainerRef}
          style={{
            flex: 1,
            border: `1px solid ${colors.border}`,
            borderRadius: '5px',
            padding: '10px',
            overflowY: 'auto',
            marginBottom: '10px',
            backgroundColor: colors.chatBg,
            opacity: isLoading ? 0.6 : 1,
            }}
          >
        {chatHistory.map((chat, index) => {
          const messageText = getMessageText(chat);
          const isAI = isAIMessage(chat);
          
          return (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '10px',
              justifyContent: isAI ? 'flex-start' : 'flex-end',
            }}
          >
            {isAI && (
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#007AAA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '10px',
                  position: 'relative',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <div style={{position: 'relative', left: -5, top:-5, width: '50px', height: '50px'}}>
                  <Icon name='ic_redaction_smart' size={50} color='white' />
                  <div style={{position:'absolute', top: 10, left:14.5, height: '18%', width:'53%', backgroundColor:'#007AAA', borderRadius:'100%'}}/>
                  <div style={{position:'absolute', top: 27, left:6.5, height: '25%', width:'18%', backgroundColor:'#007AAA', borderRadius:'100%'}}/>
                  <div style={{position:'absolute', bottom: 10, left:10, height: '18%', width:'25%', backgroundColor:'#007AAA', borderRadius:'100%'}}/>
                </div>
              </div>
            )}
            <div
              style={{
                padding: '10px',
                backgroundColor: isAI ? colors.aiBubble : colors.userBubble,
                borderRadius: '15px',
                color: colors.msgText,
                maxWidth: '70%',
                textAlign: isAI ? 'left' : 'right',
                position: 'relative',
              }}
            >
              {messageText === 'AI: Loading...' ? (
                <div style={{ position: 'relative', height: '24px', width: '480px', marginLeft: '5px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '14px'}}>
                  {printLogs(aiThinkingLog.length > 0 ? [aiThinkingLog[aiThinkingLog.length - 1]] : [])}
                  {/* Base gray icon */}
                  <div>
                    <span style={{ position: 'absolute', display: 'flex', top: 0, justifyContent: 'center', alignItems: 'center', left: '500px'}}>
                      <Icon name="ic_radio_btn" size={24} color='#ccc' />
                    </span>
                    {/* Colored icon with circular fill animation */}
                    <span style={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: '500px',
                      height: '24px',
                      width: '24px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                      <svg width="20" height="20" viewBox="0 0 20 20" style={{ animation: 'rotate 1.5s linear infinite' }}>
                        <circle
                        cx="10" 
                        cy="10" 
                        r="8" 
                        stroke="#007AAA" 
                        strokeWidth="2" 
                        fill="transparent" 
                        strokeLinecap="round"
                        strokeDasharray="40" 
                        strokeDashoffset="30"/>
                      </svg>
                    </span>
                  </div>
                  
                </div>
              ) : (
                <>
                  <Markdown>{messageText}</Markdown>
                  {/* Add feedback buttons for AI responses */}
                  {isAI && messageText !== 'AI: Loading...' && (() => {
                    const feedbackValue = aiService.extractFeedbackFromMessage(chat);
                    const isMessageLoading = feedbackLoading[index] || false;
                    const isCopied = copiedMessages[index] || false;
                    const isDisabled = isLoading || isMessageLoading;
                    
                    return (
                      <>
                        <hr className="feedback-divider" />
                        <div className="feedback-container">
                          {/* Feedback buttons on the left */}
                          <div className="feedback-buttons-group">
                            <button
                              onClick={() => handleFeedback(index, 'positive')}
                              className={`feedback-button ${feedbackValue === 'positive' ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                              title={isDisabled ? "Please wait..." : (feedbackValue === 'positive' ? "Good feedback given" : "Mark as good response")}
                              disabled={isDisabled}
                            >
                              {isMessageLoading && feedbackValue !== 'positive' ? (
                                <FeedbackSpinner />
                              ) : (
                                <img 
                                  src="/icons/ic_thumb_up.svg" 
                                  alt="Thumbs up"
                                  className={`feedback-icon ${feedbackValue === 'positive' ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                                />
                              )}
                            </button>
                            <button
                              onClick={() => handleFeedback(index, 'negative')}
                              className={`feedback-button ${feedbackValue === 'negative' ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                              title={isDisabled ? "Please wait..." : (feedbackValue === 'negative' ? "Poor feedback given" : "Mark as poor response")}
                              disabled={isDisabled}
                            >
                              {isMessageLoading && feedbackValue !== 'negative' ? (
                                <FeedbackSpinner />
                              ) : (
                                <img 
                                  src="/icons/ic_thumb_down.svg" 
                                  alt="Thumbs down"
                                  className={`feedback-icon ${feedbackValue === 'negative' ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                                />
                              )}
                            </button>
                          </div>
                          
                          {/* Copy button on the right */}
                          <button
                            className={`copy-button ${isCopied ? 'copied' : ''}`}
                            onClick={() => handleCopy(messageText, index)}
                            title={isCopied ? "Copied!" : "Copy response to clipboard"}
                          >
                            <Icon name={isCopied ? "ic_check" : "ic_copy"} size={24} color={isCopied ? "white" : colors.copyIconColor} />
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
          );
        })}
      </div>
      <div style={{ marginBottom: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {suggestions.map((suggestion, index) => (
          <button
            key={index}
            onClick={() => handleSendMessage(suggestion)}
            disabled={isLoading}
            style={{
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '6px 12px',
              gap: '6px',
              height: '34px',
              border: `1px solid ${colors.btnBorder}`,
              borderRadius: '2px',
              backgroundColor: colors.btnBg,
              color: colors.btnText,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {suggestion}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', borderTop: `1px solid ${colors.border}`, paddingTop: '10px' }}>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault(); // Prevents adding a new line
              handleSendMessage(message);
            }
          }}
          placeholder="Type your question here..."
          style={{
            flex: 1,
            padding: '10px',
            border: `1px solid ${colors.border}`,
            borderRadius: '5px',
            marginRight: '10px',
            fontSize: '16px',
            resize: 'none', // Prevent resizing
            height: '60px', // Set a fixed height
            backgroundColor: colors.inputBg,
            color: colors.inputText,
          }}
        />
        <button
          onClick={() => handleSendMessage(message)}
          disabled={isLoading}
          style={{
            padding: '10px 20px',
            backgroundColor: isLoading ? '#6c757d' : '#007BFF',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
          }}
        >
          ➤
        </button>
      </div>
    </div>
  );
}

export default ChatWithAI;
